import React, { useState, useEffect } from 'react';
import { Check, X, AlertCircle, Loader2, Link as LinkIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  validateUsernameSlug,
  checkUsernameAvailability,
  generateUsernameSuggestions,
  generateSlugFromText
} from '../lib/slugUtils';

interface UsernameManagerProps {
  userId: string;
  currentUsername: string | null;
  firstName: string;
  companyName: string;
  onUpdate?: () => void;
}

export function UsernameManager({
  userId,
  currentUsername,
  firstName,
  companyName,
  onUpdate
}: UsernameManagerProps) {
  const [username, setUsername] = useState(currentUsername || '');
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationMessage, setValidationMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!currentUsername) {
      const suggestions = generateUsernameSuggestions(firstName, companyName);
      setSuggestions(suggestions);
    }
  }, [currentUsername, firstName, companyName]);

  useEffect(() => {
    setHasChanges(username !== (currentUsername || ''));
  }, [username, currentUsername]);

  useEffect(() => {
    if (!username) {
      setValidationMessage(null);
      return;
    }

    const validation = validateUsernameSlug(username);
    if (!validation.valid) {
      setValidationMessage({ type: 'error', text: validation.error! });
      return;
    }

    if (username === currentUsername) {
      setValidationMessage({ type: 'info', text: 'Dit is de huidige username' });
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsChecking(true);
      const availability = await checkUsernameAvailability(username, supabase, userId);
      setIsChecking(false);

      if (availability.available) {
        setValidationMessage({ type: 'success', text: 'Username is beschikbaar!' });
      } else {
        setValidationMessage({ type: 'error', text: availability.error! });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [username, currentUsername, userId]);

  const handleSave = async () => {
    if (!validationMessage || validationMessage.type !== 'success') {
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({ username_slug: username })
        .eq('id', userId);

      if (error) {
        setValidationMessage({ type: 'error', text: 'Er ging iets mis bij het opslaan' });
      } else {
        setValidationMessage({ type: 'success', text: 'Username succesvol opgeslagen!' });
        setHasChanges(false);
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (error) {
      console.error('Error saving username:', error);
      setValidationMessage({ type: 'error', text: 'Er ging iets mis bij het opslaan' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (value: string) => {
    const slug = generateSlugFromText(value);
    setUsername(slug);
  };

  const applySuggestion = (suggestion: string) => {
    setUsername(suggestion);
  };

  const getValidationIcon = () => {
    if (isChecking) {
      return <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />;
    }
    if (!validationMessage) {
      return null;
    }
    if (validationMessage.type === 'success') {
      return <Check className="w-5 h-5 text-green-600" />;
    }
    if (validationMessage.type === 'error') {
      return <X className="w-5 h-5 text-red-600" />;
    }
    return <AlertCircle className="w-5 h-5 text-blue-600" />;
  };

  const canSave = hasChanges && validationMessage?.type === 'success' && !isSaving && !isChecking;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Blog domein voor lees-nu.nl
          </label>
          <div className="relative">
            <input
              type="text"
              value={username}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="bijv. henk-vandenberg"
              className="input-field pr-10"
              maxLength={30}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {getValidationIcon()}
            </div>
          </div>

          {validationMessage && (
            <p className={`text-sm mt-2 ${
              validationMessage.type === 'success' ? 'text-green-600' :
              validationMessage.type === 'error' ? 'text-red-600' :
              'text-blue-600'
            }`}>
              {validationMessage.text}
            </p>
          )}

          {username && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg flex items-center gap-2">
              <LinkIcon className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                lees-nu.nl/blog/<span className="font-semibold">{username}</span>
              </span>
            </div>
          )}
        </div>

        <button
          onClick={handleSave}
          disabled={!canSave}
          className="btn-primary mt-7 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Opslaan...' : 'Opslaan'}
        </button>
      </div>

      {!currentUsername && suggestions.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">Suggesties:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => applySuggestion(suggestion)}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-lg hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs text-gray-500 space-y-1">
        <p>• Domein moet beginnen met een letter</p>
        <p>• Alleen kleine letters, cijfers en streepjes toegestaan</p>
        <p>• Minimaal 3, maximaal 30 karakters</p>
        <p>• Dit is voor de publieke blog URL, niet voor inloggen</p>
      </div>
    </div>
  );
}
